import { computed, ref } from 'vue'
import type {
  CropRegion,
  CroppedResult,
  DocumentAdjustments,
  EditMode,
  ImageDimensions,
  ImageDisplayParams,
  SelectedRegionId
} from '../types'

export function useImageEditor(t?: (key: string) => string) {
  // Core state
  const editMode = ref<EditMode>('trim')
  const image = ref<HTMLImageElement | null>(null)
  const canvas = ref<HTMLCanvasElement | null>(null)
  const ctx = ref<CanvasRenderingContext2D | null>(null)

  // Store the original high-resolution image data URL separately
  const originalHighResImageData = ref<string>('')

  // Image dimensions and scaling
  const originalImageDimensions = ref<ImageDimensions>({ width: 0, height: 0 })
  const previewScale = ref(1)
  const canvasWidth = ref(0)
  const canvasHeight = ref(0)

  // Crop regions and results
  const cropRegions = ref<CropRegion[]>([])
  const selectedRegionId = ref<string | null>(null)
  const croppedResults = ref<CroppedResult[]>([])
  const originalCroppedResults = ref<CroppedResult[]>([])
  const baseCroppedResults = ref<CroppedResult[]>([])
  const pristineCroppedResults = ref<CroppedResult[]>([])

  // Selected region for transformations
  const selectedCroppedRegionId = ref<SelectedRegionId>(null)

  // Transformations
  const rotation = ref(0)
  const croppedResultsRotation = ref(0)
  const adjustments = ref<Record<string, DocumentAdjustments>>({})

  // Trim amounts per region
  const trimAmounts = ref<Record<string, number>>({})

  // UI state
  const showCropFeedback = ref(false)
  const showRemoveConfirmation = ref(false)
  const showExitConfirmation = ref(false)

  // Image display parameters for coordinate conversion
  const imageDisplayParams = ref<ImageDisplayParams | null>(null)

  // Computed properties
  const hasAnyAdjustments = computed(() => {
    return Object.values(adjustments.value).some(
      (adj) => adj.brightness !== 0 || adj.contrast !== 0 || adj.gamma !== 0 || adj.sharpness !== 0
    )
  })

  const hasAnyChanges = computed(() => {
    return (
      croppedResults.value.length > 0 ||
      rotation.value !== 0 ||
      croppedResultsRotation.value !== 0 ||
      hasAnyAdjustments.value ||
      Object.values(trimAmounts.value).some((amount) => amount > 0)
    )
  })

  // Utility functions
  function generateId(): string {
    return 'region_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11)
  }

  function getRegionDisplayName(regionId: string): string {
    const regionIndex = cropRegions.value.findIndex((region) => region.id === regionId)
    const pageLabel = t ? t('page') : 'Page'
    return regionIndex !== -1 ? `${pageLabel} ${regionIndex + 1}` : regionId
  }

  function resetEditorState() {
    croppedResults.value = []
    originalCroppedResults.value = []
    baseCroppedResults.value = []
    pristineCroppedResults.value = []
    showCropFeedback.value = false
    rotation.value = 0
    croppedResultsRotation.value = 0
    adjustments.value = {}
    trimAmounts.value = {}
    editMode.value = 'trim'
    cropRegions.value = []
    selectedRegionId.value = null
    selectedCroppedRegionId.value = null
  }

  function setupCanvas() {
    if (!canvas.value || !image.value) return

    // Store original dimensions
    originalImageDimensions.value = {
      width: image.value.width,
      height: image.value.height
    }

    // Get the actual container that defines available space
    // Canvas hierarchy: canvas -> centering div -> ImageEditorCanvas (p-6) -> main container (p-4 pb-16)
    const canvasContainer = canvas.value.parentElement // centering div
    const imageEditorContainer = canvasContainer?.parentElement // ImageEditorCanvas (p-6)
    const mainContainer = imageEditorContainer?.parentElement // main container (p-4 pb-16)

    if (!mainContainer) return

    // Force a reflow to ensure container has final dimensions
    mainContainer.offsetHeight

    // Get the real rendered dimensions of the main container
    const containerRect = mainContainer.getBoundingClientRect()

    // Account for all the padding in the hierarchy:
    // Main container: p-4 = 16px all sides = 32px horizontal, 32px vertical
    // ImageEditorCanvas: p-6 = 24px all sides = 48px horizontal, 48px vertical
    // Total padding: 80px horizontal, 80px vertical
    const horizontalPadding = 80
    const verticalPadding = 80
    const availableWidth = Math.max(200, containerRect.width - horizontalPadding)
    const availableHeight = Math.max(150, containerRect.height - verticalPadding)

    const aspectRatio = image.value.width / image.value.height

    let displayWidth = image.value.width
    let displayHeight = image.value.height

    // Scale to fit available container space while maintaining aspect ratio
    const scaleToFitWidth = availableWidth / displayWidth
    const scaleToFitHeight = availableHeight / displayHeight
    const scaleToFit = Math.min(scaleToFitWidth, scaleToFitHeight, 1) // Don't scale up

    displayWidth = Math.floor(displayWidth * scaleToFit)
    displayHeight = Math.floor(displayHeight * scaleToFit)

    // Ensure we don't exceed container bounds
    if (displayWidth > availableWidth) {
      displayWidth = availableWidth
      displayHeight = Math.floor(availableWidth / aspectRatio)
    }
    if (displayHeight > availableHeight) {
      displayHeight = availableHeight
      displayWidth = Math.floor(availableHeight * aspectRatio)
    }

    // Calculate scale factor for coordinate conversion
    // This is the scale from display canvas to original high-resolution image
    previewScale.value = image.value.width / displayWidth

    console.log('🖼️ Canvas Setup:', {
      container: `${containerRect.width}x${containerRect.height}`,
      available: `${availableWidth}x${availableHeight}`,
      original: `${image.value.width}x${image.value.height}`,
      display: `${displayWidth}x${displayHeight}`,
      scale: scaleToFit.toFixed(3)
    })

    // Set canvas to display size for UI interaction
    canvas.value.width = displayWidth
    canvas.value.height = displayHeight
    canvas.value.style.width = displayWidth + 'px'
    canvas.value.style.height = displayHeight + 'px'

    // Store display dimensions
    canvasWidth.value = displayWidth
    canvasHeight.value = displayHeight

    // Get context
    ctx.value = canvas.value.getContext('2d')
  }

  function loadImage(imageData: string, onImageLoaded?: () => void) {
    // Store the original high-resolution image data
    originalHighResImageData.value = imageData

    // Clear previous image
    if (image.value) {
      image.value.onload = null
      image.value.onerror = null
    }

    image.value = new Image()
    image.value.onload = () => {
      console.log(
        'Image loaded - Original dimensions:',
        image.value?.width,
        'x',
        image.value?.height
      )
      setupCanvas()
      // Call the callback to initialize crop regions and draw
      if (onImageLoaded) {
        onImageLoaded()
      }
    }
    image.value.onerror = (error) => {
      console.error('Failed to load image:', error)
    }
    image.value.src = imageData
  }

  return {
    // State
    editMode,
    image,
    canvas,
    ctx,
    originalImageDimensions,
    previewScale,
    canvasWidth,
    canvasHeight,
    cropRegions,
    selectedRegionId,
    croppedResults,
    originalCroppedResults,
    baseCroppedResults,
    pristineCroppedResults,
    selectedCroppedRegionId,
    rotation,
    croppedResultsRotation,
    adjustments,
    trimAmounts,
    showCropFeedback,
    showRemoveConfirmation,
    showExitConfirmation,
    imageDisplayParams,
    originalHighResImageData,

    // Computed
    hasAnyAdjustments,
    hasAnyChanges,

    // Methods
    generateId,
    getRegionDisplayName,
    resetEditorState,
    setupCanvas,
    loadImage
  }
}
